import React, { FC } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'

import { IconLongSmallArrowRight } from '@pijma/icon-gallery/crypto'

import { useToken } from '@hooks'
import styled from 'styled-components'

import { CaptionText500 } from '../text/BodyText'

const NavigationWrapper = styled.div`
  margin-bottom: 24px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: start;
  width: 100%;
  gap: 4px;
`

const IconArrow: FC = () => (
  <IconLongSmallArrowRight
    height={16}
    width={16}
    color={useToken().color.icon.light_bg.secondary.default.value}
  />
)

const TextWrapper = styled.div<{ color: string; isCursor: boolean }>`
  color: ${(props) => props.color};
  ${(props) => (props.isCursor ? 'cursor: pointer;' : '')}
`

interface WrapperProps {
  paymentMethodDisabled?: boolean
  dataDisabled?: boolean
  methodDisabled?: boolean
  sumDisabled?: boolean
  paymentDisabled?: boolean
  paymentMethodRedirectLink?: string
  dataRedirectLink?: string
  methodRedirectLink?: string
  sumRedirectLink?: string
  paymentRedirectLink?: string
  noPaymentMethod?: boolean
  noData?: boolean
  showMethod?: boolean
}

const NavigationBar: FC<WrapperProps> = (props: WrapperProps) => {
  const intl = useIntl()
  const primaryColor = '#000000'
  const disabledColor = useToken().color.font.light_bg.secondary.default.value
  const linkColor = '#5817BA'

  const redirectTo = useNavigate()

  const isPaymentMethodClickable = props.paymentMethodRedirectLink !== undefined
  const isMethodClickable = props.methodRedirectLink !== undefined

  const handleClick = (link?: string) => {
    if (link) {
      redirectTo(link)
    }
  }

  const textColor = (link?: string, disabled?: boolean) => {
    return link ? linkColor : disabled ? disabledColor : primaryColor
  }

  const PaymentMethod = () => (
    <>
      <TextWrapper
        color={textColor(
          props.paymentMethodRedirectLink,
          props.paymentMethodDisabled,
        )}
        isCursor={isPaymentMethodClickable}
        onClick={() => handleClick(props.paymentMethodRedirectLink)}
      >
        <CaptionText500>
          {intl.formatMessage({ id: 'navigation.paymentMethod' })}
        </CaptionText500>
      </TextWrapper>
      <IconArrow />
    </>
  )

  const Data = () => (
    <>
      <TextWrapper
        color={textColor(props.dataRedirectLink, props.dataDisabled)}
        isCursor={props.dataRedirectLink !== undefined}
        onClick={() => handleClick(props.dataRedirectLink)}
      >
        <CaptionText500>
          {intl.formatMessage({ id: 'navigation.data' })}
        </CaptionText500>
      </TextWrapper>
      <IconArrow />
    </>
  )

  const Method = () => (
    <>
      <TextWrapper
        color={textColor(props.methodRedirectLink, props.methodDisabled)}
        isCursor={isMethodClickable}
        onClick={() => handleClick(props.methodRedirectLink)}
      >
        <CaptionText500>
          {intl.formatMessage({ id: 'navigation.paymentMethod' })}
        </CaptionText500>
      </TextWrapper>
      <IconArrow />
    </>
  )

  const paymentMethod = !props.noPaymentMethod && <PaymentMethod />
  const method = props.showMethod && <Method />
  const data = !props.noData && <Data />

  return (
    <NavigationWrapper>
      {paymentMethod}
      {data}
      {method}
      <TextWrapper
        color={textColor(props.sumRedirectLink, props.sumDisabled)}
        isCursor={props.sumRedirectLink !== undefined}
        onClick={() => handleClick(props.sumRedirectLink)}
      >
        <CaptionText500>
          {intl.formatMessage({ id: 'navigation.sum' })}
        </CaptionText500>
      </TextWrapper>
      <IconArrow />
      <TextWrapper
        color={textColor(props.paymentRedirectLink, props.paymentDisabled)}
        isCursor={props.paymentRedirectLink !== undefined}
        onClick={() => handleClick(props.paymentRedirectLink)}
      >
        <CaptionText500>
          {intl.formatMessage({ id: 'navigation.payment' })}
        </CaptionText500>
      </TextWrapper>
    </NavigationWrapper>
  )
}

export default React.memo(NavigationBar)
